import { IMenuParameters, Menu } from '@Common/Controls';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import { properties } from '@Common/Core/Properties';
import { MenuItemSpacing, MenuStyle } from '@Common/Enums';
import StylesHelper from '@Common/Helpers/StylesHelper';
import { MenuAppearance, MenuComponent } from '@InstanceFactory';
import { View } from '@View/Core/View';
import { enumerable } from '@Common/Core/CoreDecorator';

export const classNameVertical = 'vertical';
export const classNameHorizontal = 'horizontal';

export class MenuLevelStyle {
    public itemDefaultStyle: string;
    public itemHoverStyle: string;
    public itemSelectedStyle: string;
    public itemOpenedStyle: string;
    public itemDefaultBorderStyle: string;
    public itemHoverBorderStyle: string;
    public itemSelectedBorderStyle: string;
    public itemOpenedBorderStyle: string;
}

export class MenuVisualizer {
    protected menuLevelStyles: MenuLevelStyle[];
    protected appearance: MenuAppearance;
    protected styleHelper: StylesHelper;
    protected menu;
    private readonly eventsBag = new MulticastDelegateBag();

    public readonly onClick = this.eventsBag.create<(item, leftToRight: boolean) => void>();
    public readonly onSelectionChanged = this.eventsBag.create<(item1, item2) => void>();

    constructor(protected component: MenuComponent, protected view: View) {
        this.appearance = component.appearance as MenuAppearance;
        this.styleHelper = view.stylesHelper;
        this.menuLevelStyles = this.getMenuLevelStyles();
    }

    protected isVertical(): boolean {
        return this.appearance.menuStyle === MenuStyle.verticalMenu || this.appearance.menuStyle === MenuStyle.popup;
    }

    protected getMenuParams(): IMenuParameters {
        const staticParams = {
            align: null,
            cloneItems: true,
            highlightAllSelectedWay: true,
            isDesktop: properties.isDesktopBrowser,
            width: this.appearance.width,
            height: this.appearance.height,
            levelStyle: this.menuLevelStyles,
            defaultLevelStyle: _.first(this.menuLevelStyles),
            allowScroll: false,
            possibleRightAlign: true
        } as IMenuParameters;

        const needApplySpacing =
            this.appearance.menuStyle === MenuStyle.horizontalMenu && this.appearance.itemSpacingType === MenuItemSpacing.specific;
        const itemSpacing = needApplySpacing ? this.appearance.itemSpacing : '';

        const menuParam: any = {
            itemSpacingType: this.appearance.itemSpacingType,
            itemSpacing: itemSpacing,
            showContainerIndicator: this.appearance.showContainerIndicator,
            isVertical: this.appearance.menuStyle === MenuStyle.verticalMenu || this.appearance.menuStyle === MenuStyle.popup,
            contentStyle: this.styleHelper.getStyleName(this.appearance.componentStyleName) + ' component-menu-children'
        };
        if (!this.isVertical() && this.appearance.align) {
            menuParam.itemsAlign = this.appearance.align;
            menuParam.align = null;
        }

        return _.assign(staticParams, menuParam);
    }

    protected getMenuLevelStyles(): MenuLevelStyle[] {
        return _.map(this.appearance.menuLevelStyles, i => {
            const mls = new MenuLevelStyle();
            mls.itemDefaultStyle = this.styleHelper.getStyleName(i.defaultStyleName);
            mls.itemHoverStyle = this.styleHelper.getStyleName(i.hoverStyleName);
            mls.itemSelectedStyle = this.styleHelper.getStyleName(i.selectedStyleName);
            mls.itemOpenedStyle = this.styleHelper.getStyleName(i.openedStyleName);

            mls.itemDefaultBorderStyle = this.styleHelper.getStyleName(i.defaultBorderStyleName);
            mls.itemHoverBorderStyle = this.styleHelper.getStyleName(i.hoverBorderStyleName);
            mls.itemSelectedBorderStyle = this.styleHelper.getStyleName(i.selectedBorderStyleName);
            mls.itemOpenedBorderStyle = this.styleHelper.getStyleName(i.openedBorderStyleName);
            return mls;
        });
    }

    public adjust(w: number, h: number): void {
        this.menu && this.menu.adjust(w, h);
    }

    @enumerable
    public get className(): string {
        if (this.isVertical()) {
            return classNameVertical;
        }
        return classNameHorizontal;
    }

    public createHandlers(): void {
        this.menu.onItemClick && this.menu.onItemClick(this.onClick);
        this.menu.onSelectionChanged && this.menu.onSelectionChanged(this.onSelectionChanged);
    }

    public createMenuComponent(): void {
        this.menu = new Menu(this.getMenuParams() as any);
    }

    public getMenu() {
        return this.menu;
    }

    public getControl(): JQuery {
        return this.menu.getControl();
    }

    public beforeProcessAction() {}

    public applyItemRules(items) {}
}
